/**
 * The pipeline switcher.
 *
 * Two repos feed this board — mirrors-collector mirrors YouTube into Postgres
 * and Elasticsearch, mirrors-classifier classifies the channels that land
 * there. They are separate services with separate failure modes, and showing
 * both at once made a long scroll in which neither was legible.
 *
 * WHY EACH ENTRY CARRIES A FIGURE AND A DOT.
 * A plain tab strip hides the pipeline you are not looking at, which is the
 * failure this board exists to catch: a sink went unwritten for 11 days on the
 * sibling pipeline and nothing surfaced it, because every panel still rendered.
 * So each entry states its own headline number and health, and stays visible
 * while the other pipeline is open. Switching changes which panels are shown —
 * never whether a problem is reported.
 */
import type { ReactNode } from 'react'
import { headline } from './format'

// vidiq joined the rail in SIL-10670. It is a third PIPELINE rather than a
// panel inside the collector's tab even though the collector image runs it:
// it has its own upstream, its own failure mode, and its own sink, and the
// rail's whole premise is that the pipeline you are NOT looking at still
// reports itself. Folding it into the collector tab would have hidden it
// behind a tab switch -- which is how its predecessor went unnoticed for three
// months while writing nothing.
//
// embeddings is the fourth, for the same reason: the DeepInfraMapper has its
// own upstream (DeepInfra), its own queue and its own failure mode -- a
// stalled mapper looks identical to a healthy one downstream, because the
// documents it never embeds simply stay pending rather than erroring loudly.
// It earns its own rail entry rather than a panel buried in whichever tab
// happens to run its worker.
// qdrant is the fifth: the QdrantMapper gateway + sgp1 vector cluster. Its
// own repo (silverpush/qdrant-deploy), its own upstream (search/write
// clients), its own failure modes (breaker, dead replicas, a silent stats
// sampler) — and the same rule as every rail entry: the tab you are NOT on
// still reports its headline and dot.
// ttd is the sixth: the TTD content pipeline (ttd_asset / ttd_iris_asset
// stage counts, ttd_source_data per-source ingestion + classification, and
// ClickHouse ctv_stats content-id match stats). Served by the collector,
// exactly like every other tab (collector/api/ttd_metrics.py, in the
// mirrors-collector repo) — that router reads the TTD content pipeline's
// own Postgres/ClickHouse DIRECTLY, with its own credentials, rather than
// calling the colleague-owned ttd_backend_api repo's own service. Its own
// upstream, its own failure mode (a stage's count silently stopping
// growing) — the same rule as every other rail entry: the tab you are NOT
// on still reports its headline and dot.
// scoring is the seventh: yt-channel-scoring's feeder -> Mongo queue -> four
// calculators (ClickHouse) -> supplier -> channel_info, plus its CSV API and
// Celery worker. Served by the collector (/metrics/scoring/*, which computes
// the findings once, server-side, for this tab, Triage, the rail and the
// alert email alike). Its failure mode is the board's oldest one: workers
// beating while nothing is scored -- so its figure is channels scored TODAY,
// which resets daily and therefore falls to nothing when scoring stops,
// rather than a lifetime total that would read normal through an outage.
export type PipelineId =
  'collector' | 'classifier' | 'vidiq' | 'embeddings' | 'qdrant' | 'ttd' | 'scoring'

export type RailEntry = {
  id: PipelineId
  name: string
  repo: string
  /** Headline figure. undefined while loading, null if it could not be read. */
  value: number | null | undefined
  /** The read SUCCEEDED and the server sent null for the figure: it cannot
   *  state it (a counter not yet written today, a fresh deploy). Rendered in
   *  the cannot-state register -- never "unavailable", which claims a failed
   *  read, and never 0, which claims a stopped pipeline. Qualifies `value:
   *  null` only; the entry builder must not set it on a failed read. */
  unstated?: boolean | undefined
  unit: string
  /** 'crit' | 'warn' | 'ok' | 'idle' — drives the dot only. */
  state: 'ok' | 'warn' | 'crit' | 'idle'
  /** Why the dot is not ok. Rendered as the entry's title for hover. */
  note?: string | undefined
}

function Figure({ value, unit, unstated }: {
  value: number | null | undefined; unit: string; unstated?: boolean | undefined
}) {
  // Three distinct states, deliberately. A dash for "not read yet" and the word
  // unavailable for "asked and failed" — collapsing them would let a dead
  // endpoint look like a slow one.
  if (value === undefined) return <span className="rail-fig rail-fig-idle">—</span>
  // And a fourth: asked, answered, and the answer was "cannot state". Dim ink
  // and a dashed rule (DESIGN.md's lead-off register), not the warning hue of
  // a failed read.
  if (value === null && unstated) {
    return (
      <span className="rail-fig rail-fig-unstated">
        <span className="unstated" title="the server cannot state this yet">cannot state</span>
      </span>
    )
  }
  if (value === null) {
    return <span className="rail-fig rail-fig-bad">unavailable</span>
  }
  return (
    <span className="rail-fig tnum">
      {headline(value)}<small>{unit}</small>
    </span>
  )
}

/** A jump target inside the active pipeline's page. */
export type RailView = { id: string; label: string }

export function PipelineTabs({ entries, active, onSelect, views, onJump }: {
  entries: RailEntry[]
  active: PipelineId
  onSelect: (id: PipelineId) => void
  /** Sections of the ACTIVE pipeline, rendered as an "On this page" group.
   *
   *  These are anchors, not tabs, and that is a decision: this board's stated
   *  premise is that switching changes which panels are shown, never whether a
   *  problem is reported — hiding sections behind view-tabs inside a pipeline
   *  would reintroduce the 11-days-unnoticed failure the rail exists to
   *  prevent. So the group navigates the page; it does not filter it. They
   *  also deliberately do NOT carry role="tab": the tablist above is the only
   *  tab semantics on the rail, and a screen reader must not hear two. */
  views?: ReadonlyArray<RailView> | undefined
  onJump?: ((id: string) => void) | undefined
}) {
  const move = (i: number, d: number) => {
    const next = entries[(i + d + entries.length) % entries.length]
    if (next) onSelect(next.id)
  }

  return (
    <nav className="rail" aria-label="Pipelines">
      <div className="rail-h">Pipelines</div>
      <div className="rail-tabs" role="tablist" aria-orientation="vertical">
        {entries.map((e, i) => (
          <button
            key={e.id}
            role="tab"
            id={`tab-${e.id}`}
            aria-controls={`panel-${e.id}`}
            aria-selected={active === e.id}
            // Only the selected tab is in the tab order; arrows move between
            // them. This is the roving-tabindex the tablist role implies.
            tabIndex={active === e.id ? 0 : -1}
            className={`rail-tab rail-${e.id}`}
            title={e.note ?? undefined}
            onClick={() => onSelect(e.id)}
            onKeyDown={ev => {
              const d = ev.key === 'ArrowDown' || ev.key === 'ArrowRight' ? 1
                      : ev.key === 'ArrowUp' || ev.key === 'ArrowLeft' ? -1 : 0
              if (!d) return
              ev.preventDefault()
              move(i, d)
            }}
          >
            <span className="rail-top">
              <span className={`rail-dot s-${e.state}`} aria-hidden="true" />
              <span className="rail-name">{e.name}</span>
            </span>
            <span className="rail-repo mono">{e.repo}</span>
            <Figure value={e.value} unit={e.unit} unstated={e.unstated} />
            {/* The dot is decorative; the state has to reach a screen reader
                some other way, and it must not be visible twice. */}
            <span className="sr-only">
              {e.state === 'ok' ? 'healthy' : e.note ?? e.state}
            </span>
          </button>
        ))}
      </div>
      {views && views.length > 0 && onJump ? (
        <>
          <div className="rail-h rail-h-views">On this page</div>
          <nav className="rail-views" aria-label="Sections of this pipeline">
            {views.map((v) => (
              <button key={v.id} className="rail-view" onClick={() => onJump(v.id)}>
                {v.label}
              </button>
            ))}
          </nav>
        </>
      ) : null}
      <p className="rail-note">
        Each pipeline keeps its own queries and failure state, so the one you are
        not viewing still reports itself here.
      </p>
    </nav>
  )
}

export function Panel({ id, active, children }: {
  id: PipelineId; active: PipelineId; children: ReactNode
}) {
  const shown = id === active
  return (
    <section
      role="tabpanel"
      id={`panel-${id}`}
      aria-labelledby={`tab-${id}`}
      hidden={!shown}
    >
      {/* Unmounted rather than hidden: a hidden panel's queries would keep
          running, and this board's whole premise is that nothing polls and
          every figure costs a real read. */}
      {shown ? children : null}
    </section>
  )
}
